import * as fs from "node:fs";
import * as path from "node:path";

import {
  CliError,
  EXIT_INPUT,
  EXIT_OK,
  EXIT_USAGE,
  scriptJsonPath,
  type CommandOutput,
} from "../../common.js";
import {
  GENERATED_SOURCE,
  scriptExportDocument,
  targetExportDocument,
} from "../../domain/authoring/v3/export.js";
import { serializeAuthoring } from "../../domain/authoring/v3/serializer.js";
import type { AuthoringDocument, AuthoringTarget } from "../../domain/authoring/v3/types.js";
import { requireScriptV3 } from "../../domain/script/v3/parse.js";
import type { EpisodeV3, SceneV3, ScriptV3 } from "../../domain/script/v3/types.js";
import {
  requireStructurallyReadableScriptDocument,
  type ScriptDocument,
} from "../../domain/script/dispatch.js";
import { requireStructurallyReadableScriptV4 } from "../../domain/script/v4/parse.js";
import { serializeAuthoringV4 } from "../../domain/authoring/v4/serializer.js";
import { strOf, type Dict } from "../../domain/script/shared.js";
import { atomicWriteText } from "../../infra/script-working-copy.js";

export type AuthoringExportKind =
  | "script"
  | "episodes"
  | "episode-spans"
  | "scenes"
  | "scene-spans"
  | "action-spans";

type Region = { from: string; through: string };
type SceneEntry = { episodeId: string; scene: SceneV3 };

function blocked(code: string, message: string, received: string[], next: string, exitCode = EXIT_INPUT): never {
  throw new CliError(message, {
    exitCode,
    errorCode: code,
    hint: next,
    data: { actual: received },
  });
}

function readScript(opts: Dict): ScriptDocument {
  const scriptPath = scriptJsonPath(opts);
  let value: unknown;
  try {
    value = JSON.parse(fs.readFileSync(scriptPath, "utf-8"));
  } catch (error) {
    blocked("SCRIPT_JSON_UNAVAILABLE", "The local script cannot be read as JSON.", [
      scriptPath,
      error instanceof Error ? error.message : String(error),
    ], "Provide a readable local Script v3 file.");
  }
  return requireStructurallyReadableScriptDocument(value);
}

function regions(raw: string): Region[] {
  if (!raw) blocked("AUTHORING_SELECTOR_REQUIRED", "This export kind requires --in.", ["<missing>"], "Pass one or more comma-separated point or from..through regions.", EXIT_USAGE);
  return raw.split(",").map((part) => {
    const value = part.trim();
    if (!value) blocked("AUTHORING_SELECTOR_INVALID", "Selector contains an empty region.", [raw], "Remove empty comma-separated regions.", EXIT_USAGE);
    const delimiter = value.indexOf("..");
    if (delimiter < 0) return { from: value, through: value };
    if (value.indexOf("..", delimiter + 2) >= 0) blocked("AUTHORING_SELECTOR_INVALID", "Selector region has more than one range delimiter.", [value], "Use point or from..through.", EXIT_USAGE);
    const from = value.slice(0, delimiter);
    const through = value.slice(delimiter + 2);
    if (!from || !through) blocked("AUTHORING_SELECTOR_INVALID", "Selector range endpoint is empty.", [value], "Provide both endpoints.", EXIT_USAGE);
    return { from, through };
  });
}

function rangeIndices(ids: string[], region: Region, label: string): [number, number] {
  if (region.from === "*" && region.through === "*") {
    if (ids.length === 0) blocked("AUTHORING_SELECTOR_EMPTY", `The script contains no ${label}.`, ["*"], "Choose a non-empty target kind.");
    return [0, ids.length - 1];
  }
  if (ids.filter((id) => id === region.from).length > 1
    || ids.filter((id) => id === region.through).length > 1) {
    blocked("AUTHORING_SELECTOR_AMBIGUOUS", `${label} selector endpoint is not unique.`, [region.from, region.through], "Repair duplicate stable IDs before exporting this target.");
  }
  const from = ids.indexOf(region.from);
  const through = ids.indexOf(region.through);
  if (from < 0 || through < 0) blocked("AUTHORING_SELECTOR_NOT_FOUND", `${label} selector endpoint was not found.`, [region.from, region.through], "Use IDs from the current local script.");
  if (from > through) blocked("AUTHORING_SELECTOR_REVERSED", `${label} selector is reversed in current document order.`, [region.from, region.through], "Put the earlier document endpoint first.");
  return [from, through];
}

function uniqueTargets<T>(values: T[], key: (value: T) => string): T[] {
  const seen = new Set<string>();
  for (const value of values) {
    const id = key(value);
    if (seen.has(id)) blocked("AUTHORING_SELECTOR_OVERLAP", "Selector regions overlap or repeat a target.", [id], "Use non-overlapping regions.");
    seen.add(id);
  }
  return values;
}

function assertIncreasing(coordinates: readonly number[][], label: string): void {
  for (let index = 1; index < coordinates.length; index += 1) {
    const previous = coordinates[index - 1]!;
    const current = coordinates[index]!;
    const length = Math.max(previous.length, current.length);
    let comparison = 0;
    for (let part = 0; part < length; part += 1) {
      comparison = (current[part] ?? 0) - (previous[part] ?? 0);
      if (comparison !== 0) break;
    }
    if (comparison <= 0) {
      blocked("AUTHORING_SELECTOR_ORDER", `${label} selector regions are not in current document order.`, [
        `region=${index + 1}`,
      ], "List disjoint selector regions from earlier to later document positions.");
    }
  }
}

function episodeDocument(script: ScriptV3, raw: string): AuthoringDocument {
  const selected: EpisodeV3[] = [];
  const ids = script.episodes.map((episode) => episode.episode_id);
  for (const region of regions(raw)) {
    const [from, through] = rangeIndices(ids, region, "episode");
    selected.push(...script.episodes.slice(from, through + 1));
  }
  uniqueTargets(selected, (episode) => episode.episode_id);
  assertIncreasing(selected.map((episode) => [ids.indexOf(episode.episode_id)]), "Episode");
  const targets: Array<Extract<AuthoringTarget, { kind: "episode" }>> = selected.map((episode) => ({
    kind: "episode",
    value: structuredClone(episode),
    source: GENERATED_SOURCE,
  }));
  return targetExportDocument("episode", targets);
}

function episodeSpanDocument(script: ScriptV3, raw: string): AuthoringDocument {
  const ids = script.episodes.map((episode) => episode.episode_id);
  const targets: Array<Extract<AuthoringTarget, { kind: "episode-span" }>> = [];
  const occupied = new Set<number>();
  const coordinates: number[][] = [];
  for (const region of regions(raw)) {
    const [from, through] = rangeIndices(ids, region, "episode");
    for (let index = from; index <= through; index += 1) {
      if (occupied.has(index)) blocked("AUTHORING_SELECTOR_OVERLAP", "Episode span regions overlap.", [`${region.from}..${region.through}`], "Use non-overlapping regions.");
      occupied.add(index);
    }
    targets.push({
      kind: "episode-span",
      from: ids[from]!,
      through: ids[through]!,
      episodes: structuredClone(script.episodes.slice(from, through + 1)),
      source: GENERATED_SOURCE,
    });
    coordinates.push([from]);
  }
  assertIncreasing(coordinates, "Episode span");
  return targetExportDocument("episode-span", targets);
}

function sceneEntries(script: ScriptV3): SceneEntry[] {
  return script.episodes.flatMap((episode) => episode.scenes.map((scene) => ({
    episodeId: episode.episode_id,
    scene,
  })));
}

function sceneAddress(entry: SceneEntry): string {
  return `${entry.episodeId}/${entry.scene.scene_id}`;
}

function sceneDocument(script: ScriptV3, raw: string): AuthoringDocument {
  const entries = sceneEntries(script);
  const ids = entries.map(sceneAddress);
  const selected: SceneEntry[] = [];
  for (const region of regions(raw)) {
    const [from, through] = rangeIndices(ids, region, "scene");
    selected.push(...entries.slice(from, through + 1));
  }
  uniqueTargets(selected, sceneAddress);
  assertIncreasing(selected.map((entry) => [ids.indexOf(sceneAddress(entry))]), "Scene");
  const targets: Array<Extract<AuthoringTarget, { kind: "scene" }>> = selected.map((entry) => ({
    kind: "scene",
    parent: entry.episodeId,
    value: structuredClone(entry.scene),
    source: GENERATED_SOURCE,
  }));
  return targetExportDocument("scene", targets);
}

function parseSceneAddress(raw: string): { episodeId: string; sceneId: string } {
  const match = /^([^/#]+)\/([^/#]+)$/.exec(raw);
  if (!match) blocked("AUTHORING_SELECTOR_INVALID", "Scene selector endpoint must be ep/scn.", [raw], "Use ep_ID/scn_ID.");
  return { episodeId: match[1]!, sceneId: match[2]! };
}

function sceneSpanDocument(script: ScriptV3, raw: string): AuthoringDocument {
  const targets: Array<Extract<AuthoringTarget, { kind: "scene-span" }>> = [];
  const occupied = new Set<string>();
  const coordinates: number[][] = [];
  for (const region of regions(raw)) {
    if (region.from === "*" || region.through === "*") blocked("AUTHORING_SELECTOR_INVALID", "A scene span wildcard has no single parent.", ["*"], "Use one explicit region per episode.");
    const fromAddress = parseSceneAddress(region.from);
    const throughAddress = parseSceneAddress(region.through);
    if (fromAddress.episodeId !== throughAddress.episodeId) blocked("AUTHORING_SELECTOR_CROSS_PARENT", "A scene span must remain inside one episode.", [region.from, region.through], "Split it into one region per episode.");
    const matchingEpisodes = script.episodes.filter((candidate) => candidate.episode_id === fromAddress.episodeId);
    if (matchingEpisodes.length === 0) blocked("AUTHORING_SELECTOR_NOT_FOUND", "Scene span parent episode was not found.", [fromAddress.episodeId], "Use an existing episode ID.");
    if (matchingEpisodes.length > 1) blocked("AUTHORING_SELECTOR_AMBIGUOUS", "Scene span parent episode is not unique.", [fromAddress.episodeId], "Repair duplicate stable IDs before exporting.");
    const episode = matchingEpisodes[0]!;
    const ids = episode.scenes.map((scene) => scene.scene_id);
    const [from, through] = rangeIndices(ids, { from: fromAddress.sceneId, through: throughAddress.sceneId }, "scene");
    for (let index = from; index <= through; index += 1) {
      const address = `${episode.episode_id}/${ids[index]}`;
      if (occupied.has(address)) blocked("AUTHORING_SELECTOR_OVERLAP", "Scene span regions overlap.", [address], "Use non-overlapping regions.");
      occupied.add(address);
    }
    targets.push({
      kind: "scene-span",
      parent: episode.episode_id,
      from: ids[from]!,
      through: ids[through]!,
      scenes: structuredClone(episode.scenes.slice(from, through + 1)),
      source: GENERATED_SOURCE,
    });
    coordinates.push([script.episodes.indexOf(episode), from]);
  }
  assertIncreasing(coordinates, "Scene span");
  return targetExportDocument("scene-span", targets);
}

function parseActionAddress(raw: string): { parent: string; index: number } {
  const match = /^([^/#]+\/[^/#]+)#(\d+)$/.exec(raw);
  if (!match) blocked("AUTHORING_SELECTOR_INVALID", "Action selector endpoint must be ep/scn#index.", [raw], "Use a zero-based ep_ID/scn_ID#index address.");
  const index = Number(match[2]);
  if (!Number.isSafeInteger(index)) blocked("AUTHORING_SELECTOR_INVALID", "Action index is outside the safe integer range.", [raw], "Use a zero-based safe integer index.");
  return { parent: match[1]!, index };
}

function actionSpanDocument(script: ScriptV3, raw: string): AuthoringDocument {
  const targets: Array<Extract<AuthoringTarget, { kind: "action-span" }>> = [];
  const occupied = new Set<string>();
  const coordinates: number[][] = [];
  for (const region of regions(raw)) {
    if (region.from === "*" || region.through === "*") blocked("AUTHORING_SELECTOR_INVALID", "An action span wildcard has no single parent.", ["*"], "Use one explicit region per scene.");
    const fromAddress = parseActionAddress(region.from);
    const throughAddress = parseActionAddress(region.through);
    if (fromAddress.parent !== throughAddress.parent) blocked("AUTHORING_SELECTOR_CROSS_PARENT", "An action span must remain inside one scene.", [region.from, region.through], "Split it into one region per scene.");
    const sceneAddressValue = parseSceneAddress(fromAddress.parent);
    const matchingEpisodes = script.episodes.filter((candidate) => candidate.episode_id === sceneAddressValue.episodeId);
    if (matchingEpisodes.length === 0) blocked("AUTHORING_SELECTOR_NOT_FOUND", "Action span parent episode was not found.", [sceneAddressValue.episodeId], "Use an existing episode ID.");
    if (matchingEpisodes.length > 1) blocked("AUTHORING_SELECTOR_AMBIGUOUS", "Action span parent episode is not unique.", [sceneAddressValue.episodeId], "Repair duplicate stable IDs before exporting.");
    const episode = matchingEpisodes[0]!;
    const matchingScenes = episode.scenes.filter((candidate) => candidate.scene_id === sceneAddressValue.sceneId);
    if (matchingScenes.length === 0) blocked("AUTHORING_SELECTOR_NOT_FOUND", "Action span parent scene was not found.", [fromAddress.parent], "Use an existing scene address.");
    if (matchingScenes.length > 1) blocked("AUTHORING_SELECTOR_AMBIGUOUS", "Action span parent scene is not unique.", [fromAddress.parent], "Repair duplicate stable IDs before exporting.");
    const scene = matchingScenes[0]!;
    if (fromAddress.index > throughAddress.index) blocked("AUTHORING_SELECTOR_REVERSED", "Action span is reversed.", [region.from, region.through], "Put the lower current action index first.");
    if (throughAddress.index >= scene.actions.length) blocked("AUTHORING_SELECTOR_NOT_FOUND", "Action span endpoint is outside the current body.", [region.through, `actions=${scene.actions.length}`], "Use current zero-based action indices.");
    for (let index = fromAddress.index; index <= throughAddress.index; index += 1) {
      const address = `${fromAddress.parent}#${index}`;
      if (occupied.has(address)) blocked("AUTHORING_SELECTOR_OVERLAP", "Action span regions overlap.", [address], "Use non-overlapping regions.");
      occupied.add(address);
    }
    targets.push({
      kind: "action-span",
      parent: fromAddress.parent,
      from: fromAddress.index,
      through: throughAddress.index,
      actions: structuredClone(scene.actions.slice(fromAddress.index, throughAddress.index + 1)),
      source: GENERATED_SOURCE,
    });
    coordinates.push([
      script.episodes.indexOf(episode),
      episode.scenes.indexOf(scene),
      fromAddress.index,
    ]);
  }
  assertIncreasing(coordinates, "Action span");
  return targetExportDocument("action-span", targets);
}

export function commandExportAuthoring(kind: AuthoringExportKind, opts: Dict): [CommandOutput, number] {
  const outputRaw = strOf(opts["output"]).trim();
  if (!outputRaw) blocked("AUTHORING_OUTPUT_REQUIRED", "Export requires --output.", ["<missing>"], "Pass a destination .scriptctl file.", EXIT_USAGE);
  const script = readScript(opts);
  const selector = strOf(opts["in"]).trim();
  const outputPath = path.resolve(outputRaw);
  if (script.version === 4) {
    const exported = serializeAuthoringV4(requireStructurallyReadableScriptV4(script), kind, selector);
    atomicWriteText(outputPath, exported.content);
    return [{
      text: `Exported ${exported.targetCount} ${kind} target(s) to ${outputPath}`,
      data: {
        op: `export.${kind}`,
        output: outputPath,
        target_kind: kind === "episodes" ? "episode"
          : kind === "episode-spans" ? "episode-span"
            : kind === "scenes" ? "scene"
              : kind === "scene-spans" ? "scene-span"
                : kind === "action-spans" ? "action-span"
                  : "script",
        target_count: exported.targetCount,
        bytes: Buffer.byteLength(exported.content),
        script_version: 4,
      },
    }, EXIT_OK];
  }
  const scriptV3 = requireScriptV3(script);
  let document: AuthoringDocument;
  if (kind === "script") document = scriptExportDocument(scriptV3);
  else if (kind === "episodes") document = episodeDocument(scriptV3, selector);
  else if (kind === "episode-spans") document = episodeSpanDocument(scriptV3, selector);
  else if (kind === "scenes") document = sceneDocument(scriptV3, selector);
  else if (kind === "scene-spans") document = sceneSpanDocument(scriptV3, selector);
  else document = actionSpanDocument(scriptV3, selector);
  const content = serializeAuthoring(document);
  atomicWriteText(outputPath, content);
  const report: CommandOutput = {
    text: `Exported ${document.targets.length} ${kind} target(s) to ${outputPath}`,
    data: {
      op: `export.${kind}`,
      output: outputPath,
      target_kind: document.targetKind,
      target_count: document.targets.length,
      bytes: Buffer.byteLength(content),
      script_version: 3,
    },
  };
  return [report, EXIT_OK];
}
